// ⚠️ This file is over 150 lines. Consider splitting it.
import React from 'react'

const EventFormContent = ({ formData, onChange, errorFields = [] }) => {
    const handleRangeChange = (index, field, value) => {
        const updatedRanges = [...formData.eventDateRanges]
        updatedRanges[index][field] = value
        onChange('eventDateRanges', updatedRanges)

        // Update summary startDate/endDate
        const valid = updatedRanges.filter((r) => r.startDate && r.endDate)
        if (valid.length > 0) {
            const sorted = valid.slice().sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
            const startDate = sorted[0].startDate
            const endDate = sorted.reduce((max, curr) =>
                new Date(curr.endDate) > new Date(max.endDate) ? curr : max
            ).endDate
            onChange('startDate', startDate)
            onChange('endDate', endDate)
        }
    }

    const addRange = () => {
        const updated = [...formData.eventDateRanges, { startDate: '', endDate: '' }]
        onChange('eventDateRanges', updated)
    }

    const removeRange = (index) => {
        const updated = formData.eventDateRanges.filter((_, i) => i !== index)
        onChange('eventDateRanges', updated)
    }

    return (
        <div className="event-form-content space-y-6">
            <div>
                <label className="block font-semibold mb-1" htmlFor="title">
                    Event Title {errorFields.includes('title') && <span className="text-error">*</span>}
                </label>
                <input
                    type="text"
                    name="title"
                    id="title"
                    placeholder="e.g., Summer Block Party"
                    value={formData.title}
                    onChange={(e) => {
                        const safeValue = e.target.value.replace(/[^a-zA-Z0-9\s'-]/g, '')
                        onChange(e.target.name, safeValue)
                    }}
                    className={`w-full bg-white border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-accent ${errorFields.includes('title') ? 'border-error' : ''}`}
                    maxLength={50}
                    required
                />
                <p className="text-sm text-muted mt-1">
                    Maximum 50 characters. Remaining: {50 - formData.title.length}
                </p>
            </div>

            <div>
                <label className="block font-semibold mb-1" htmlFor="description">
                    Description {errorFields.includes('description') && <span className="text-error">*</span>}
                </label>
                <textarea
                    name="description"
                    id="description"
                    placeholder="Briefly describe your event (e.g., live music, family-friendly)"
                    value={formData.description}
                    onChange={(e) => onChange(e.target.name, e.target.value)}
                    rows="5"
                    className={`w-full bg-white border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-accent ${errorFields.includes('description') ? 'border-error' : ''}`}
                    required
                />
            </div>

            <div>
                <label className="block font-semibold mb-2">Date & Time Ranges</label>
                <p className="text-sm text-muted mb-3">
                    If your event spans multiple days or specific periods, please add each date and time range below so your audience knows exactly when to attend.
                </p>
                {formData.eventDateRanges.map((range, index) => (
                <div key={index} className="space-y-4 mb-6">
                    <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium">Start</label>
                            <input
                                type="datetime-local"
                                value={range.startDate}
                                onChange={(e) => handleRangeChange(index, 'startDate', e.target.value)}
                                className="w-full bg-white border border-gray-300 px-4 py-2 rounded"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium">End</label>
                            <input
                                type="datetime-local"
                                value={range.endDate}
                                onChange={(e) => handleRangeChange(index, 'endDate', e.target.value)}
                                className="w-full bg-white border border-gray-300 px-4 py-2 rounded"
                            />
                        </div>
                    </div>
                    {index > 0 && (
                        <div className="text-right">
                            <button
                                type="button"
                                onClick={() => removeRange(index)}
                                className="btn-destructive px-2 py-1 text-sm"
                            >
                                Remove Range
                            </button>
                        </div>
                    )}
                </div>
                ))}
                <button
                    type="button"
                    onClick={addRange}
                    className="btn-secondary mt-2">
                    + Add Another Date/Time Range
                </button>
                {/* Network Promotion Period */}
                <div className="mt-6">
                    <label className="block font-semibold mb-1">
                        Promotion Period on Network *
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium">Start</label>
                            <input
                                type="datetime-local"
                                required
                                value={formData.promotionStartDate}
                                onChange={(e) => onChange('promotionStartDate', e.target.value)}
                                className="w-full bg-white border border-gray-300 px-4 py-2 rounded"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium">End</label>
                            <input
                                type="datetime-local"
                                required
                                value={formData.promotionEndDate}
                                onChange={(e) => onChange('promotionEndDate', e.target.value)}
                                className="w-full bg-white border border-gray-300 px-4 py-2 rounded"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <label className="block font-semibold mb-1" htmlFor="location">
                    Location (optional)
                </label>
                <input
                    type="text"
                    name="location"
                    id="location"
                    placeholder="123 Main St, City, State"
                    value={formData.location}
                    onChange={(e) => onChange(e.target.name, e.target.value)}
                    className="w-full bg-white border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-accent"
                />
            </div>

            <div>
                <label className="block font-semibold mb-1" htmlFor="website">
                    Event Website (optional)
                </label>
                <input
                    type="url"
                    name="website"
                    id="website"
                    placeholder="https://example.com"
                    value={formData.website}
                    onChange={(e) => onChange(e.target.name, e.target.value)}
                    className="w-full bg-white border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-accent"
                />
            </div>
        </div>
    )
}

export default EventFormContent
